// "Import AI response" in the owner-only LegalWatch report (templates/articles/legal-watch-status.ejs):
// a pasted or loaded JSON response is checked on the server (LegalWatchReviews.ts preview route,
// nothing saved) and shown as proposed changes; only then can the owner approve, which posts the
// same response for validation and saving. Any edit after the preview hides the approval again.
interface PreviewRow {
    event: string;
    current: string;
    proposed: string;
    changed: boolean;
    confidence: string;
    uncertainties: string[];
    justification: string;
    legalSources: { url: string; provision: string }[];
    reviewedAt: string;
}

export function initLegalWatchImport(form: HTMLFormElement): void {
    const response = form.querySelector<HTMLTextAreaElement>('[data-legal-watch-response]')!;
    const file = form.querySelector<HTMLInputElement>('[data-legal-watch-file]')!;
    const previewButton = form.querySelector<HTMLButtonElement>('[data-legal-watch-preview]')!;
    const errors = form.querySelector<HTMLUListElement>('[data-legal-watch-errors]')!;
    const table = form.querySelector<HTMLTableElement>('[data-legal-watch-preview-table]')!;
    const approve = form.querySelector<HTMLButtonElement>('[data-legal-watch-approve]')!;

    const reset = () => {
        errors.hidden = true;
        table.hidden = true;
        approve.hidden = true;
    };
    const showErrors = (messages: string[]) => {
        errors.replaceChildren(...messages.map((message) => Object.assign(document.createElement('li'), { textContent: message })));
        errors.hidden = false;
    };
    const cell = (...content: (string | Node)[]) => {
        const td = document.createElement('td');
        td.append(...content);
        return td;
    };
    const link = ({ url, provision }: PreviewRow['legalSources'][number]) =>
        Object.assign(document.createElement('a'), { href: url, textContent: provision, target: '_blank', rel: 'noopener noreferrer' });

    previewButton.hidden = false;
    response.addEventListener('input', reset);
    file.addEventListener('change', async () => {
        const chosen = file.files?.[0];
        if (!chosen) return;
        response.value = await chosen.text();
        reset();
    });

    previewButton.addEventListener('click', async () => {
        reset();
        if (!response.reportValidity()) return;
        const fields = [...new FormData(form)].filter((entry): entry is [string, string] => typeof entry[1] === 'string');
        try {
            const reply = await fetch(form.dataset.previewAction!, { method: 'POST', body: new URLSearchParams(fields) });
            const data = (await reply.json()) as { errors?: string[]; rows?: PreviewRow[] };
            if (!data.rows) return showErrors(data.errors ?? [form.dataset.failed!]);
            table.tBodies[0].replaceChildren(
                ...data.rows.map((row) => {
                    const tr = document.createElement('tr');
                    const sources = row.legalSources.flatMap((source, index) => (index ? ['; ', link(source)] : [link(source)]));
                    tr.append(
                        cell(row.event),
                        cell(row.current),
                        cell(row.changed ? row.proposed : `${row.proposed} (${form.dataset.unchanged})`),
                        cell([row.confidence, ...row.uncertainties].join(' · ')),
                        cell(row.justification),
                        cell(...sources),
                        cell(row.reviewedAt),
                    );
                    return tr;
                }),
            );
            table.hidden = false;
            approve.hidden = false;
        } catch {
            showErrors([form.dataset.failed!]);
        }
    });
}
